import React from "react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader, CTASection } from "@/components/Sections";
import { Reveal, TechLabel } from "@/components/Reveal";

const PROGRAMS = [
  { id: "trials", num: "01", tag: "90-Day Part Trials", copy: "Test BlackSilex against your existing benchmark with documented performance." },
  { id: "consignment", num: "02", tag: "Consignment Programs", copy: "Strategic inventory positioned for remote operations and invoiced only when used." },
  { id: "rebuild", num: "03", tag: "Rebuild & Exchange", copy: "Turn component failure into a scheduled exchange." },
  { id: "response", num: "04", tag: "Rapid Response", copy: "A direct response line for urgent machine-down situations." },
];

const TIMELINE = ["Identify", "Source", "Position", "Respond", "Install", "Measure"];

export default function Uptime() {
  useSEO({
    title: "Mining Equipment Parts & Uptime Support | BlackSilex",
    description: "Mining equipment parts backed by uptime support — 90-day part trials, consignment programs, rebuild and exchange, and rapid response to keep machinery running.",
    path: "/uptime",
  });

  return (
    <div data-testid="uptime-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Uptime Programs" }]}
        eyebrow="Uptime Programs"
        titleLines={["Parts arrive", "before production", "stops."]}
        intro="Parts are only useful when they arrive before production stops. Our programs are engineered around your downtime math."
      />

      {/* Programs */}
      <section id="supply" className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 lg:py-24 scroll-mt-24">
        <div className="grid md:grid-cols-2 border-t border-l border-offwhite/10">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.1}>
              <div id={p.id} className="group relative p-8 lg:p-12 border-r border-b border-offwhite/10 hover:bg-nearblack transition-colors scroll-mt-28 h-full" data-testid={`program-${p.id}`}>
                <span className="font-mono-tech text-xs text-strike">{p.num}</span>
                <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-offwhite mt-6">{p.tag}</h2>
                <p className="text-mutedgray mt-5 leading-relaxed max-w-md">{p.copy}</p>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-strike group-hover:w-full transition-all duration-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Process timeline */}
      <section className="bg-nearblack border-y border-offwhite/10 py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10">
          <TechLabel>The Process</TechLabel>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl md:text-6xl leading-[0.95] mt-6 mb-16">
            <Reveal>How a part becomes uptime.</Reveal>
          </h2>
          <div className="relative">
            <div className="absolute left-0 right-0 top-[27px] h-px bg-offwhite/10 hidden md:block" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-4">
              {TIMELINE.map((step, i) => (
                <Reveal key={step} delay={i * 0.1}>
                  <div className="relative" data-testid={`timeline-${i}`}>
                    <div className="w-14 h-14 border border-offwhite/20 bg-coal flex items-center justify-center relative z-10">
                      <span className="font-mono-tech text-sm text-strike">0{i + 1}</span>
                    </div>
                    <p className="font-display text-lg font-bold uppercase text-offwhite mt-6">{step}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
